import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext(null);

export const translations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      education: 'Education',
      projects: 'Projects',
      services: 'Services',
      skills: 'Skills',
      contact: 'Contact',
      getInTouch: 'Get In Touch',
    },
    hero: {
      badge: 'Available for freelance & contract',
      greeting: "Hi, I'm",
      viewWork: 'View My Work',
      getInTouch: 'Get In Touch',
      features: [
        {
          title: 'IT Operations',
          desc: 'Stable, secure day-to-day technology—run with discipline so the business rarely feels the seams.',
        },
        {
          title: 'Infrastructure',
          desc: 'Servers, networks, storage, and cloud—designed and maintained for resilience, clarity, and room to grow.',
        },
        {
          title: 'Full Stack Development',
          desc: 'Internal tools and web applications shaped around real workflows—not slide decks.',
        },
      ],
    },
    about: {
      title: 'About',
      highlight: 'Me',
      subtitle: 'Operational leadership, hands-on engineering, and the through-line between them.',
      workExp: 'Work Experience',
      certifications: 'Licenses & Certifications',
      issued: 'Issued',
      expires: 'Expires',
    },
    education: {
      title: 'Education &',
      highlight: 'Development',
      subtitle: 'Formal grounding in informatics—and the discipline to keep sharpening the craft.',
    },
    projects: {
      title: 'Featured',
      highlight: 'Projects',
      subtitle: 'Interactive demos and builds—stack and scope summarized for quick review.',
      screenshotTip: 'For a clean capture without navbar/footer, open the site with',
      bannerTitle: 'These demos showcase what I can build for you',
      bannerDesc:
        'Each project is an example of a real capability—not a finished product. I can design and build a custom solution tailored to your business needs. Feel free to reach out via',
      bannerEmail: 'email',
      bannerOr: 'or',
      bannerWhatsapp: 'WhatsApp (chat only)',
      keyFeatures: 'Key Features:',
      code: 'Code',
      demo: 'Demo',
      demoUnavailable: 'Demo not available',
      demoUnavailableDesc: 'Set the project liveUrl in .env or mock.js.',
      ctaText: "Interested in a custom build? Let's talk about your project.",
      emailMe: 'Email Me',
      whatsapp: 'WhatsApp (chat only)',
      categories: {
        all: 'All',
        HRMS: 'HRMS',
        Booking: 'Booking',
        'IT Operations': 'IT Operations',
        DevOps: 'DevOps',
        Travel: 'Travel',
        Finance: 'Finance',
        'E-Commerce': 'E-Commerce',
        'Mobile App': 'Mobile App',
        LMS: 'LMS',
        'CMS & IoT': 'CMS & IoT',
        'Web App': 'Web App',
        'Job Portal': 'Job Portal',
        Healthcare: 'Healthcare',
        WordPress: 'WordPress',
      },
    },
    heroCta: {
      badge: 'Open for new projects',
      title: 'What I Can',
      highlight: 'Build For You',
      subtitle: 'Available on Fastwork & Fiverr — IT infrastructure, server deployment, and full-stack web development.',
      hireOnFastwork: 'Hire on Fastwork',
      orderOnFiverr: 'Order on Fiverr',
      chatWhatsApp: 'Chat on WhatsApp',
      services: [
        { title: 'IT Infrastructure & Ops', desc: 'Server setup, monitoring, networking' },
        { title: 'VPS & Linux Server', desc: 'VPS deploy, SSL, domain configuration' },
        { title: 'DevOps & CI/CD', desc: 'Docker, GitHub Actions, Prometheus' },
        { title: 'Full Stack Development', desc: 'React, Next.js, Node.js, REST API' },
        { title: 'WordPress & Elementor', desc: 'Landing pages, themes, plugins' },
        { title: 'System Administration', desc: 'Windows Server, Linux, Active Directory' },
      ],
    },
    services: {
      title: 'Freelance',
      highlight: 'Services',
      subtitle: 'Available on leading freelance platforms for IT infrastructure, server setup, and web development.',
      viewProfile: 'View Profile',
      orderNow: 'Order Now',
      availableOn: 'Available on',
    },
    skills: {
      title: 'Skills &',
      highlight: 'Expertise',
      subtitle:
        'Where operations meet infrastructure—and where both meet product-quality software.',
      testimonialTitle: 'What colleagues notice',
      testimonialSubtitle:
        "Anonymous notes from people I've supported—ops, infrastructure, and delivery.",
      privateClient: 'Private client',
    },
    contact: {
      title: 'Get In',
      highlight: 'Touch',
      subtitle: "Let's work together on your next project",
      description:
        "Looking to build something? I'm open to discussing custom projects, freelance work, or technical collaborations.",
      infoTitle: 'Contact Information',
      email: 'Email',
      phone: 'Phone',
      location: 'Location',
      availability: 'Availability',
      directTitle: 'Reach me directly',
      sendEmail: 'Send an Email',
      whatsapp: 'WhatsApp (chat only)',
      linkedin: 'Connect on LinkedIn',
      formTitle: 'Send Me a Message',
      formSubtitle: "I'll get back to you as soon as possible",
      namePlaceholder: 'Your Name',
      emailPlaceholder: 'Your Email',
      messagePlaceholder: 'Your Message',
      sendButton: 'Send Message',
      contactSubjectPrefix: 'Portfolio Contact from',
    },
    footer: {
      quickLinks: 'Quick Links',
      getInTouch: 'Get In Touch',
      madeWith: 'Made with',
      andCreativity: 'and creativity',
    },
  },
  id: {
    nav: {
      home: 'Beranda',
      about: 'Tentang',
      education: 'Pendidikan',
      projects: 'Proyek',
      services: 'Layanan',
      skills: 'Keahlian',
      contact: 'Kontak',
      getInTouch: 'Hubungi Saya',
    },
    hero: {
      badge: 'Tersedia untuk proyek freelance & kontrak',
      greeting: 'Halo, saya',
      viewWork: 'Lihat Proyek Saya',
      getInTouch: 'Hubungi Saya',
      features: [
        {
          title: 'Operasi IT',
          desc: 'Teknologi yang stabil dan aman setiap hari—dikelola dengan disiplin agar bisnis berjalan tanpa gangguan.',
        },
        {
          title: 'Infrastruktur',
          desc: 'Server, jaringan, storage, dan cloud—dirancang dan dikelola untuk ketahanan jangka panjang.',
        },
        {
          title: 'Full Stack Development',
          desc: 'Tools internal dan aplikasi web yang dibangun mengikuti cara kerja nyata—bukan sekadar konsep di atas kertas.',
        },
      ],
    },
    about: {
      title: 'Tentang',
      highlight: 'Saya',
      subtitle: 'Kepemimpinan operasional, engineering langsung, dan benang merah yang menghubungkan keduanya.',
      workExp: 'Pengalaman Kerja',
      certifications: 'Lisensi & Sertifikasi',
      issued: 'Diterbitkan',
      expires: 'Berlaku hingga',
    },
    education: {
      title: 'Pendidikan &',
      highlight: 'Pengembangan',
      subtitle: 'Dasar akademis di bidang informatika—dan komitmen untuk terus berkembang lewat praktik nyata.',
    },
    projects: {
      title: 'Proyek',
      highlight: 'Unggulan',
      subtitle: 'Demo interaktif dan hasil build nyata—stack dan scope dirangkum untuk referensi cepat.',
      screenshotTip: 'Untuk screenshot bersih tanpa navbar/footer, buka situs dengan',
      bannerTitle: 'Demo ini menunjukkan apa yang bisa saya bangun untuk Anda',
      bannerDesc:
        'Setiap proyek mencerminkan kemampuan nyata—bukan produk jadi. Saya bisa merancang dan membangun solusi custom sesuai kebutuhan bisnis Anda. Hubungi saya lewat',
      bannerEmail: 'email',
      bannerOr: 'atau',
      bannerWhatsapp: 'WhatsApp (chat saja)',
      keyFeatures: 'Fitur Utama:',
      code: 'Kode',
      demo: 'Demo',
      demoUnavailable: 'Demo belum tersedia',
      demoUnavailableDesc: 'Atur liveUrl proyek di .env atau mock.js.',
      ctaText: 'Tertarik dengan solusi custom? Yuk diskusikan proyek Anda.',
      emailMe: 'Kirim Email',
      whatsapp: 'WhatsApp (chat saja)',
      categories: {
        all: 'Semua',
        HRMS: 'HRMS',
        Booking: 'Booking',
        'IT Operations': 'Operasi IT',
        DevOps: 'DevOps',
        Travel: 'Travel',
        Finance: 'Keuangan',
        'E-Commerce': 'E-Commerce',
        'Mobile App': 'Mobile App',
        LMS: 'LMS',
        'CMS & IoT': 'CMS & IoT',
        'Web App': 'Web App',
        'Job Portal': 'Portal Karir',
        Healthcare: 'Kesehatan',
        WordPress: 'WordPress',
      },
    },
    heroCta: {
      badge: 'Terbuka untuk proyek baru',
      title: 'Yang Bisa Saya',
      highlight: 'Bangun Untuk Anda',
      subtitle: 'Tersedia di Fastwork & Fiverr — infrastruktur IT, deploy server, dan pengembangan web full-stack.',
      hireOnFastwork: 'Pesan di Fastwork',
      orderOnFiverr: 'Order di Fiverr',
      chatWhatsApp: 'Chat WhatsApp',
      services: [
        { title: 'Infrastruktur IT & Operasi', desc: 'Setup server, monitoring, jaringan' },
        { title: 'VPS & Server Linux', desc: 'Deploy VPS, SSL, konfigurasi domain' },
        { title: 'DevOps & CI/CD', desc: 'Docker, GitHub Actions, Prometheus' },
        { title: 'Full Stack Development', desc: 'React, Next.js, Node.js, REST API' },
        { title: 'WordPress & Elementor', desc: 'Landing page, tema, plugin' },
        { title: 'System Administration', desc: 'Windows Server, Linux, Active Directory' },
      ],
    },
    services: {
      title: 'Layanan',
      highlight: 'Freelance',
      subtitle: 'Tersedia di platform freelance terpercaya untuk kebutuhan infrastruktur IT, setup server, dan pengembangan web.',
      viewProfile: 'Lihat Profil',
      orderNow: 'Pesan Sekarang',
      availableOn: 'Tersedia di',
    },
    skills: {
      title: 'Keahlian &',
      highlight: 'Kompetensi',
      subtitle: 'Di mana operasi IT bertemu infrastruktur—dan keduanya bertemu software berkualitas.',
      testimonialTitle: 'Yang kolega saya amati',
      testimonialSubtitle:
        'Catatan anonim dari rekan yang pernah bekerja bersama saya—di bidang ops, infrastruktur, dan delivery.',
      privateClient: 'Klien private',
    },
    contact: {
      title: 'Hubungi',
      highlight: 'Saya',
      subtitle: 'Mari bekerja sama untuk proyek Anda berikutnya',
      description:
        'Ada project yang ingin dibangun? Saya terbuka untuk diskusi project custom, pekerjaan freelance, atau kolaborasi teknis.',
      infoTitle: 'Informasi Kontak',
      email: 'Email',
      phone: 'Telepon',
      location: 'Lokasi',
      availability: 'Ketersediaan',
      directTitle: 'Hubungi saya langsung',
      sendEmail: 'Kirim Email',
      whatsapp: 'WhatsApp (chat saja)',
      linkedin: 'Connect di LinkedIn',
      formTitle: 'Kirim Pesan',
      formSubtitle: 'Saya akan balas secepatnya',
      namePlaceholder: 'Nama Anda',
      emailPlaceholder: 'Email Anda',
      messagePlaceholder: 'Pesan Anda',
      sendButton: 'Kirim Pesan',
      contactSubjectPrefix: 'Kontak dari',
    },
    footer: {
      quickLinks: 'Navigasi',
      getInTouch: 'Hubungi Saya',
      madeWith: 'Dibuat dengan',
      andCreativity: 'dan semangat berkarya',
    },
  },
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState('id');
  const t = translations[language];
  const toggleLanguage = () => setLanguage((l) => (l === 'en' ? 'id' : 'en'));

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
}
